import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import SetupWizard from "./SetupWizard";

interface SetupControllerProps {
  children: React.ReactNode;
}

const SetupController: React.FC<SetupControllerProps> = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [setupCompleted, setSetupCompleted] = useState(true); // Default to true to prevent flashing
  const [userId, setUserId] = useState<string | null>(null);

  // Check if the user has completed the setup
  useEffect(() => {
    const checkSetupStatus = async () => {
      try {
        setLoading(true);
        console.log("🔍 SetupController: Checking setup status...");
        
        // Get the current user
        const { data: { user } } = await supabase.auth.getUser();
        
        if (!user) {
          console.error("❌ SetupController: No user found");
          setLoading(false);
          return;
        }
        
        console.log("✅ SetupController: Found user:", user.id);
        setUserId(user.id);
        
        // Check if the user has completed the setup
        const { data, error } = await supabase
          .from("brands")
          .select("setup_completed")
          .eq("id", user.id)
          .single();
        
        if (error) {
          console.error("❌ SetupController: Error fetching brand setup status:", error);
          return;
        }
        
        console.log("🔍 SetupController: Setup completed status:", data?.setup_completed);
        
        // If setup_completed is null or false, show the setup wizard
        setSetupCompleted(data?.setup_completed || false);
      } catch (error) {
        console.error("❌ SetupController: Error checking setup status:", error);
      } finally {
        setLoading(false);
      }
    };
    
    checkSetupStatus();
  }, []);

  const handleSetupComplete = async () => {
    try {
      if (!userId) return;
      
      console.log("✏️ SetupController: Completing setup for user:", userId);
      
      // Mark setup as completed
      const { error } = await supabase
        .from("brands")
        .update({ setup_completed: true })
        .eq("id", userId);
      
      if (error) {
        console.error("❌ SetupController: Error updating setup status:", error);
        return;
      }
      
      console.log("✅ SetupController: Setup completed successfully");
      setSetupCompleted(true);
    } catch (error) {
      console.error("❌ SetupController: Error marking setup as complete:", error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Show setup wizard if setup is not completed
  if (!setupCompleted) {
    console.log("🧙‍♂️ SetupController: Showing setup wizard");
    return <SetupWizard onComplete={handleSetupComplete} />;
  }

  // Show the children (dashboard) if setup is completed
  console.log("👶 SetupController: Showing children (dashboard)");
  return <>{children}</>;
};

export default SetupController; 